import React, { useEffect } from 'react';
import Button from './Button';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { Loader2 } from 'lucide-react';

interface InfiniteLoadMoreProps {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  /** Whether the list already has items (avoid showing "end" on empty states) */
  hasItems?: boolean;
  className?: string;
  /** If true, auto-load when sentinel becomes visible (Notion-like) */
  auto?: boolean;
  /** Show a subtle "end of list" message when hasMore=false */
  showEndMessage?: boolean;
  endMessage?: string;
}

const InfiniteLoadMore: React.FC<InfiniteLoadMoreProps> = ({
  hasMore,
  loading,
  onLoadMore,
  hasItems = true,
  className = '',
  auto = true,
  showEndMessage = true,
  endMessage = 'Tout est chargé',
}) => {
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0,
    rootMargin: '600px',
    triggerOnce: false,
  });

  useEffect(() => {
    if (!auto) return;
    if (!hasMore || loading) return;
    if (isIntersecting) onLoadMore();
  }, [auto, hasMore, loading, isIntersecting, onLoadMore]);

  if (!hasMore) {
    if (!showEndMessage || !hasItems) return null;
    return (
      <div className={`flex items-center justify-center py-4 ${className}`}>
        <span className="text-xs text-gray-400">{endMessage}</span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center py-4 ${className}`}>
      {/* Sentinel for auto-loading */}
      <div ref={elementRef} className="w-full h-px" aria-hidden="true" />

      {/* Standard 2026: single loading indicator (no duplicated spinner + button) */}
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Chargement…</span>
        </div>
      ) : (
        // Fallback button (useful on mobile / if observer is blocked)
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onLoadMore}
          className="min-w-[140px]"
        >
          Charger plus
        </Button>
      )}
    </div>
  );
};

export default InfiniteLoadMore;

